import { ChevronDown, SquareArrowUpRight, Printer, FileText, TriangleAlert } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { badgeClasses, convertISODateToMMDDYYYY, formatCurrency } from '@/lib/utils';
import { OneLineText } from '@/components/OneLineText';
import { useNavigate } from 'react-router-dom';
import { hasFeature } from '@/services/featureService';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  exportVendorInvoicesPdfs,
  getVendorInvoices,
  useMarkVendorInvoicesFailed,
  useVendorInvoiceFilterOptions,
} from '@/services/vendorPaymentService';
import {
  VendorInvoicePaymentStatus,
  type VendorInvoiceItem,
  type VendorInvoicesQueryParams,
} from '@/types/vendorPayment.types';
import { AgingBracket } from '@/types/accountsReceivable.types';
import { Feature } from '@/utils/constant';
import VendorApplyPaymentModal from '@/components/VendorApplyPaymentModal';
import VendorInfoCard from '@/components/VendorInfoCard';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { TooltipContent } from '@/components/ui/tooltip';
import type { AxiosError } from 'axios';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';

type AgingType = 'PAID' | 'LESS_THAN_30' | '30_DAY' | '60_DAY' | '90_DAY';

const badges: Record<AgingType, { label: string; className: string }> = {
  LESS_THAN_30: {
    label: 'Unpaid',
    className: 'bg-[#FFEEBF] text-[#6C4A09] hover:bg-[#FFEEBF]',
  },
  PAID: {
    label: 'Paid',
    className: 'bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3]',
  },
  '30_DAY': {
    label: 'Unpaid',
    className: 'bg-[#FFE0E0] text-[#A41919] hover:bg-[#FFE0E0]',
  },
  '60_DAY': {
    label: 'Unpaid',
    className: 'bg-[#FFAD0D] text-[#0A0A0A] hover:bg-[#FFAD0D]',
  },
  '90_DAY': {
    label: 'Unpaid',
    className: 'bg-[#A41919] text-[#F5F5F5] hover:bg-[#A41919]',
  },
};

const VendorPayment = () => {
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [tableData, setTableData] = useState<{ data: VendorInvoiceItem[]; rest?: unknown }>();
  const [selectedAgingBracket, setSelectedAgingBracket] = useState<AgingBracket | undefined>(undefined);
  const [isApplyPaymentModalOpen, setIsApplyPaymentModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openVendorInfoId, setOpenVendorInfoId] = useState<string | null>(null);
  const [invoiceIdToVendorMap, setInvoiceIdToVendorMap] = useState<Map<string, string>>(new Map());
  const navigate = useNavigate();
  const hasApplyPaymentFeature = hasFeature(Feature.VENDOR_PAYMENT_APPLY);

  const [summaryData, setSummaryData] = useState<{
    invoiceIds?: string[];
    totalAmountDue?: string;
    past30DaysDue?: string;
    past60DaysDue?: string;
    past90DaysDue?: string;
  }>({});

  const filterOptionsData = useVendorInvoiceFilterOptions();
  const { vendorOptions, invoiceOptions, fileNumberOptions } = useMemo(() => {
    return {
      vendorOptions: filterOptionsData.data?.vendors ?? [],
      invoiceOptions: filterOptionsData.data?.invoiceNumbers ?? [],
      fileNumberOptions: filterOptionsData.data?.fileNumbers ?? [],
    };
  }, [filterOptionsData.data]);

  const apiGetWrapper = useCallback((params: Record<string, unknown>) => {
    const apiParams: VendorInvoicesQueryParams = {
      ...params,
      pageNo: params.pageNo as number,
      pageSize: params.pageSize as number,
    };
    const result = getVendorInvoices(apiParams);
    return {
      data: result.data,
      totalRows: result.totalRows,
      isFetching: result.isFetching,
      invoiceNumbers: result.invoiceNumbers ?? [],
      totalAmountDue: result.totalAmountDue,
      past30DaysDue: result.past30DaysDue,
      past60DaysDue: result.past60DaysDue,
      past90DaysDue: result.past90DaysDue,
    };
  }, []);

  useEffect(() => {
    if (tableData?.rest) {
      const response = tableData.rest as {
        totalAmountDue?: string;
        past30DaysDue?: string;
        past60DaysDue?: string;
        past90DaysDue?: string;
      };
      const allIdsOnPage = tableData.data?.map((d) => d.processBillingVendorEntryId) ?? [];

      // Update the global invoice-to-vendor map
      const newMap = new Map(invoiceIdToVendorMap);
      tableData.data?.forEach((invoice) => {
        newMap.set(invoice.processBillingVendorEntryId, invoice.vendorId);
      });
      setInvoiceIdToVendorMap(newMap);

      if (selectAll) {
        // When selectAll is active, only select same-vendor ids from this page
        if (selectedInvoiceIds.length > 0) {
          const currentVendorId = newMap.get(selectedInvoiceIds[0]);
          if (currentVendorId) {
            const sameVendorIds = allIdsOnPage.filter((id) => newMap.get(id) === currentVendorId);
            setSelectedInvoiceIds((prev) => {
              const existingSameVendorIds = prev.filter((id) => newMap.get(id) === currentVendorId);
              return [...new Set([...existingSameVendorIds, ...sameVendorIds])];
            });
          } else {
            setSelectedInvoiceIds(allIdsOnPage);
          }
        } else {
          setSelectedInvoiceIds(allIdsOnPage);
        }
      }
      setSummaryData({
        invoiceIds: allIdsOnPage,
        totalAmountDue: response.totalAmountDue,
        past30DaysDue: response.past30DaysDue,
        past60DaysDue: response.past60DaysDue,
        past90DaysDue: response.past90DaysDue,
      });
    }
  }, [tableData, selectAll]);

  const handleCheckboxChange = (id: string, isChecked: string | boolean) => {
    if (isChecked) {
      const newInvoiceVendorId = invoiceIdToVendorMap.get(id);
      if (newInvoiceVendorId && selectedInvoiceIds.length > 0) {
        // Check vendor constraint using the global map
        const currentVendorId = invoiceIdToVendorMap.get(selectedInvoiceIds[0]);
        if (currentVendorId && newInvoiceVendorId !== currentVendorId) {
          toast({
            variant: 'destructive',
            description: 'All selected invoices must belong to the same vendor',
          });
          return;
        }
      }
      setSelectedInvoiceIds((prev) => [...prev, id]);
    } else {
      setSelectedInvoiceIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleSelectAllChange = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (isChecked) {
      const allIds = summaryData.invoiceIds ?? [];
      if (selectedInvoiceIds.length > 0) {
        // Use the global map to enforce vendor constraint
        const currentVendorId = invoiceIdToVendorMap.get(selectedInvoiceIds[0]);
        if (currentVendorId) {
          const sameVendorIds = allIds.filter((id) => invoiceIdToVendorMap.get(id) === currentVendorId);
          setSelectedInvoiceIds((prev) => {
            const existingSameVendorIds = prev.filter((id) => invoiceIdToVendorMap.get(id) === currentVendorId);
            return [...new Set([...existingSameVendorIds, ...sameVendorIds])];
          });
          return;
        }
      }
      if (allIds.length > 0) {
        const firstInvoiceVendorId = invoiceIdToVendorMap.get(allIds[0]);
        if (firstInvoiceVendorId) {
          const sameVendorIds = allIds.filter((id) => invoiceIdToVendorMap.get(id) === firstInvoiceVendorId);
          setSelectedInvoiceIds(sameVendorIds);
          return;
        }
      }
      setSelectedInvoiceIds(allIds);
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const selectedInvoicesData = useMemo(() => {
    return (
      tableData?.data
        ?.filter((row) => selectedInvoiceIds.includes(row.processBillingVendorEntryId))
        .map((row) => ({
          processBillingVendorEntryId: row.processBillingVendorEntryId,
          vendorInvoiceNumber: row.vendorInvoiceNumber,
          amount: row.amount,
          vendorId: row.vendorId,
          paymentStatus: row.paymentStatus,
        })) ?? []
    );
  }, [tableData, selectedInvoiceIds]);

  const handleSingleApplyPayment = useCallback((invoice: VendorInvoiceItem) => {
    if (invoice.paymentStatus === VendorInvoicePaymentStatus.UNPAID) {
      setSelectedInvoiceIds([invoice.processBillingVendorEntryId]);
      setIsApplyPaymentModalOpen(true);
    }
  }, []);

  const markFailedMutation = useMarkVendorInvoicesFailed();

  const handleApplyPaymentSuccess = useCallback(() => {
    setSelectedInvoiceIds([]);
    setSelectAll(false);
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleBulkExport = useCallback(async () => {
    if (selectedInvoiceIds.length === 0) {
      toast({
        description: 'Please select at least one invoice to export',
      });
      return;
    }
    const single =
      selectedInvoiceIds.length === 1
        ? tableData?.data?.find((d) => d.processBillingVendorEntryId === selectedInvoiceIds[0])
        : undefined;
    await exportVendorInvoicesPdfs({ invoiceIds: selectedInvoiceIds }, single?.vendorInvoiceNumber);
  }, [selectedInvoiceIds, tableData]);

  const handleMarkAsFailed = useCallback(() => {
    if (selectedInvoiceIds.length === 0) {
      toast({
        description: 'Please select at least one invoice to mark as failed',
      });
      return;
    }
    const hasPaidInvoice = selectedInvoicesData.some(
      (inv) => inv.paymentStatus === VendorInvoicePaymentStatus.PAID
    );
    if (hasPaidInvoice) {
      toast({
        variant: 'destructive',
        description: 'Cannot mark paid invoices as failed',
      });
      return;
    }
    markFailedMutation.mutate(
      { invoiceIds: selectedInvoiceIds, isFailedTransaction: true },
      {
        onSuccess: (data) => {
          toast({ description: data.message });
          setSelectedInvoiceIds([]);
          setSelectAll(false);
          setRefreshKey((prev) => prev + 1);
        },
        onError: (error: AxiosError<GenericErrorResponse>) => {
          displayError(error.response?.data);
        },
      }
    );
  }, [selectedInvoiceIds, selectedInvoicesData, markFailedMutation]);

  const handleBulkPayment = useCallback(() => {
    if (selectedInvoicesData.length === 0) {
      toast({
        variant: 'destructive',
        description: 'Please select at least one unpaid invoice for payment',
      });
      return;
    }
    const paidExists = selectedInvoicesData.some((inv) => inv.paymentStatus === VendorInvoicePaymentStatus.PAID);
    if (paidExists) {
      toast({
        variant: 'destructive',
        description: 'Cannot apply payment to paid invoices',
      });
      return;
    }
    const uniqueVendorIds = new Set(selectedInvoicesData.map((inv) => inv.vendorId));
    if (uniqueVendorIds.size > 1) {
      toast({
        variant: 'destructive',
        description: 'All selected invoices must belong to the same vendor',
      });
      return;
    }
    setIsApplyPaymentModalOpen(true);
  }, [selectedInvoicesData]);

  const bulkActions = useMemo(
    () => [
      ...(hasApplyPaymentFeature
        ? [
            {
              label: 'Bulk Payment',
              value: 'bulkPayment',
              icon: <SquareArrowUpRight className="w-4 h-4" color="#334155" strokeWidth="1px" />,
              onClick: handleBulkPayment,
            },
          ]
        : []),
      {
        label: 'Export Invoice(s)',
        value: 'exportInvoices',
        icon: <Printer className="w-4 h-4" color="#334155" strokeWidth="1px" />,
        onClick: handleBulkExport,
      },
      ...(hasApplyPaymentFeature
        ? [
            {
              label: 'Mark as Payment Failed',
              value: 'markAsFailed',
              icon: <FileText className="w-4 h-4" color="#334155" strokeWidth="1px" />,
              onClick: handleMarkAsFailed,
            },
          ]
        : []),
    ],
    [hasApplyPaymentFeature, handleBulkPayment, handleBulkExport, handleMarkAsFailed]
  );

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'vendorIds',
        label: 'Search Vendor',
        placeholder: 'Search Vendor',
        fieldType: 'MULTISELECT',
        multiSelectOptions: vendorOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => {
          const today = new Date();
          return date > today;
        },
        dependsOn: {
          field: 'fromDate',
          resetOnChange: true,
          disableBefore: true,
        },
      },
      {
        name: 'invoiceNumbers',
        label: 'Search Invoice Number',
        placeholder: 'Search Invoice Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: invoiceOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
      {
        name: 'fileNumbers',
        label: 'Search File Number',
        placeholder: 'Search File Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: fileNumberOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
    ],
    [vendorOptions, invoiceOptions, fileNumberOptions]
  );

  const viewFilters = {
    buttonLabel: 'View',
    groups: [
      {
        label: 'Payment Status',
        name: 'status',
        options: [
          { label: 'Paid', value: 'PAID' },
          { label: 'Unpaid', value: 'UNPAID' },
        ],
      },
    ],
  };

  const handleAgingBracketClick = useCallback((bracket: AgingBracket) => {
    setSelectedAgingBracket((prev) => (prev === bracket ? undefined : bracket));
  }, []);

  const summarySection = useMemo(
    () => (
      <div className="w-full flex justify-between flex-col md:flex-row gap-4">
        <div className="w-[50%] flex justify-between flex-col md:justify-start gap-1.5">
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Amount Due: </span>
            <span className="text-[#551175] text-sm font-[500] ">
              {summaryData.totalAmountDue ? formatCurrency(parseFloat(summaryData.totalAmountDue)) : '$0.00'}
            </span>
          </div>
          <div className="flex gap-3 flex-col md:flex-row">
            <button
              type="button"
              className={`flex gap-1 cursor-pointer hover:opacity-80 bg-transparent border-none p-0 ${
                selectedAgingBracket === AgingBracket.DAY_30 ? 'font-[500] underline text-xs' : 'text-xs'
              }`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_30)}
              aria-pressed={selectedAgingBracket === AgingBracket.DAY_30}
            >
              <span className="font-[500] text-xs">30 Days: </span>
              <span className="text-[#551175] text-xs font-[500] ">
                {summaryData.past30DaysDue ? formatCurrency(parseFloat(summaryData.past30DaysDue)) : '$0.00'}
              </span>
            </button>
            <button
              type="button"
              className={`flex gap-1 cursor-pointer hover:opacity-80 bg-transparent border-none p-0 ${
                selectedAgingBracket === AgingBracket.DAY_60 ? 'font-[500] underline text-xs' : 'text-xs'
              }`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_60)}
              aria-pressed={selectedAgingBracket === AgingBracket.DAY_60}
            >
              <span className="font-[500] text-xs">60 Days: </span>
              <span className="text-[#FFAD0D] text-xs font-[500] ">
                {summaryData.past60DaysDue ? formatCurrency(parseFloat(summaryData.past60DaysDue)) : '$0.00'}
              </span>
            </button>
            <button
              type="button"
              className={`flex gap-1 cursor-pointer hover:opacity-80 bg-transparent border-none p-0 ${
                selectedAgingBracket === AgingBracket.DAY_90 ? 'font-[500] underline text-xs' : 'text-xs'
              }`}
              onClick={() => handleAgingBracketClick(AgingBracket.DAY_90)}
              aria-pressed={selectedAgingBracket === AgingBracket.DAY_90}
            >
              <span className="font-[500] text-xs">90 Days: </span>
              <span className="text-[#A41919] text-xs font-[500] ">
                {summaryData.past90DaysDue ? formatCurrency(parseFloat(summaryData.past90DaysDue)) : '$0.00'}
              </span>
            </button>
          </div>
          <div>
            <span className="text-sm">Showing this week&apos;s and overdue invoices.</span>
          </div>
        </div>
        <div className="flex flex-col items-end">
          {bulkActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className="focus:border-[#1551B2] !border-[1px] !border-primary min-w-[136px] w-fit h-[36px] !text-primary font-[500] text-sm"
              >
                <Button variant="outline" className="flex flex-row gap-2 align-middle ">
                  <span>Bulk Actions</span>
                  <ChevronDown className="w-4 h-4" strokeWidth="1px" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 mr-8">
                {bulkActions.map((action) => {
                  let isDisabled = false;
                  if (selectedInvoiceIds.length === 0) {
                    isDisabled = true;
                  } else if (action.value === 'bulkPayment') {
                    const uniqueVendorIds = new Set(selectedInvoicesData.map((inv) => inv.vendorId));
                    const hasPaid = selectedInvoicesData.some(
                      (inv) => inv.paymentStatus === VendorInvoicePaymentStatus.PAID
                    );
                    isDisabled = uniqueVendorIds.size > 1 || hasPaid;
                  } else if (action.value === 'markAsFailed') {
                    const hasPaid = selectedInvoicesData.some(
                      (inv) => inv.paymentStatus === VendorInvoicePaymentStatus.PAID
                    );
                    isDisabled = hasPaid;
                  }
                  return (
                    <DropdownMenuItem
                      key={action.value}
                      onSelect={isDisabled ? undefined : action.onClick}
                      disabled={isDisabled}
                    >
                      <div className="!flex !w-full !flex-row justify-between">
                        <span className="text-primary text-sm font-[500]">{action.label}</span>
                        <span>{action.icon}</span>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    ),
    [summaryData, selectedAgingBracket, handleAgingBracketClick, bulkActions]
  );

  const columns: ColumnDef<VendorInvoiceItem>[] = useMemo(() => {
    return [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all"
            checked={selectAll}
            onCheckedChange={(checked) => handleSelectAllChange(checked === true)}
          />
        ),
        size: 60,
        cell: ({ row }) => {
          const id = row.original.processBillingVendorEntryId;
          const isChecked = selectedInvoiceIds.includes(id);
          return (
            <div className="flex w-full min-w-[40px] mr-5">
              <Checkbox
                id={`vendor-invoice-${id}`}
                checked={isChecked}
                onCheckedChange={(checked) => handleCheckboxChange(id, checked === true)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'date',
        header: 'Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const date = cell.getValue() as string;
          return (
            <div className="flex w-full min-w-[120px] mr-5">
              <span>{convertISODateToMMDDYYYY(date)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'vendorInvoiceNumber',
        header: 'Invoice #',
        enableSorting: true,
        cell: ({ cell }) => {
          const invoiceNumber = cell.getValue() as string;
          const workOrderId = cell.row.original.workOrderId;
          return (
            <button
              type="button"
              className="flex items-center w-full min-w-[120px] cursor-pointer hover:text-focuscolor text-left bg-transparent border-none p-0"
              onClick={() => {
                if (workOrderId) {
                  navigate(`/process/workorder/${workOrderId}/process-billing-vendor`);
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && workOrderId) {
                  e.preventDefault();
                  navigate(`/process/workorder/${workOrderId}/process-billing-vendor`);
                }
              }}
            >
              <span className="hover:underline">{invoiceNumber}</span>
            </button>
          );
        },
      },
      {
        accessorKey: 'workOrderNumber',
        header: 'W/O #',
        cell: ({ cell }) => {
          const workOrderNumber = cell.getValue() as string | null;
          return <OneLineText text={workOrderNumber ?? '-'} className="w-full min-w-[100px] mr-5" />;
        },
      },
      {
        accessorKey: 'vendorName',
        header: 'Vendor',
        enableSorting: true,
        cell: ({ row }) => {
          const vendor = row.original;
          return (
            <button
              type="button"
              className="flex items-center w-full min-w-[160px] cursor-pointer hover:text-focuscolor text-left bg-transparent border-none p-0"
              onClick={() => setOpenVendorInfoId(vendor.vendorId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpenVendorInfoId(vendor.vendorId);
                }
              }}
            >
              <span className="hover:underline">{vendor.vendorName}</span>
            </button>
          );
        },
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex w-full min-w-[120px] mr-5">
            <span>{formatCurrency(row.original.amount)}</span>
          </div>
        ),
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.paymentStatus;
          const agingBracket = row.original.aging?.bracket as AgingType | undefined;
          const isFailed = row.original.isFailedTransaction;
          const badgeConfig =
            status === VendorInvoicePaymentStatus.PAID
              ? badges.PAID
              : agingBracket && agingBracket in badges
                ? badges[agingBracket]
                : badges['LESS_THAN_30'];
          return (
            <div className="flex w-full min-w-[120px] justify-start items-center gap-[5px]">
              <Badge className={`${badgeConfig.className} ${badgeClasses}`}>{badgeConfig.label}</Badge>
              {isFailed && <TriangleAlert className="text-[#D72222] h-4 w-4" />}
            </div>
          );
        },
      },
      {
        accessorKey: 'checkNumber',
        header: 'Check #',
        cell: ({ row }) => {
          const checkNumber = row.original.checkNumber;
          const isUnpaid = row.original.paymentStatus === VendorInvoicePaymentStatus.UNPAID;
          if (!isUnpaid) {
            return (
              <div className="flex w-full min-w-[120px] mr-5">
                <span>{checkNumber ?? '-'}</span>
              </div>
            );
          }
          if (!hasApplyPaymentFeature) {
            return (
              <div className="flex w-full min-w-[120px] mr-5">
                <span>-</span>
              </div>
            );
          }
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex flex-row items-center w-[130px] gap-2 cursor-pointer"
                    onClick={() => handleSingleApplyPayment(row.original)}
                  >
                    <span className="text-xs text-[#1551B2]">Apply Payment</span>
                    <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Apply Payment to Vendor Invoice</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
    ];
  }, [
    selectAll,
    selectedInvoiceIds,
    handleSelectAllChange,
    navigate,
    handleSingleApplyPayment,
    hasApplyPaymentFeature,
  ]);

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        toolbar={{
          toolBarTitle: 'Vendor Payment',
          filters: viewFilters,
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => setTableData({ data: data as VendorInvoiceItem[], rest })}
        search={searchConfig}
        summarySection={summarySection}
        filters={selectedAgingBracket ? { agingBracket: selectedAgingBracket } : undefined}
        key={refreshKey}
      />

      <VendorApplyPaymentModal
        open={isApplyPaymentModalOpen}
        onOpenChange={setIsApplyPaymentModalOpen}
        invoices={selectedInvoicesData}
        onSuccess={handleApplyPaymentSuccess}
      />

      {openVendorInfoId && <VendorInfoCard vendorId={openVendorInfoId} onClose={() => setOpenVendorInfoId(null)} />}
    </div>
  );
};

export default VendorPayment;
